import {
  debounce,
  throttle,
  prefersReducedMotion,
} from '@theme/utilities';

/**
 * A slightly longer fallback prevents mobile momentum scrolling from being
 * reported as finished between frames while still keeping controls responsive.
 *
 * @constant {number}
 */
const SCROLL_END_TIMEOUT = 90;

/**
 * Class representing a Scroller that handles smooth scrolling and detects
 * scroll end events.
 */
export class Scroller {
  /** @type {HTMLElement} */
  element;

  /** @type {Promise<void> | undefined} */
  #promise = undefined;

  /** @type {(() => void) | undefined} */
  #resolve = undefined;

  /** @type {() => void} */
  #throttledCallback;

  /** @type {() => void} */
  #endCallback;

  /** @type {(() => void) | undefined} */
  #onScrollInit;

  /** @type {(() => void) | undefined} */
  #onScrollEnd;

  /** @type {boolean} */
  #userEvent = true;

  /** @type {boolean} */
  #ignore = false;

  /** @type {boolean} */
  #isScrolling = false;

  /** @type {boolean} */
  #supportsNativeScrollEnd = false;

  /** @type {boolean} */
  #lastScrollWasUserEvent = true;

  /**
   * @param {HTMLElement} element
   * @param {Object} options
   * @param {() => void} options.onScroll
   * @param {() => void} [options.onScrollStart]
   * @param {() => void} [options.onScrollEnd]
   */
  constructor(element, options) {
    const onScroll =
      typeof options.onScroll === 'function'
        ? options.onScroll
        : () => {};

    this.#throttledCallback = throttle(
      onScroll,
      SCROLL_END_TIMEOUT
    );
    this.#endCallback = onScroll;
    this.#onScrollInit = options.onScrollStart;
    this.#onScrollEnd = options.onScrollEnd;

    this.element = element;
    this.#supportsNativeScrollEnd =
      'onscrollend' in element;

    this.element.addEventListener(
      'scroll',
      this.#handleScroll,
      { passive: true }
    );

    if (this.#supportsNativeScrollEnd) {
      this.element.addEventListener(
        'scrollend',
        this.#handleNativeScrollEnd
      );
    }

    this.element.addEventListener(
      'pointerdown',
      this.#markUserInput,
      { passive: true }
    );
    this.element.addEventListener(
      'touchstart',
      this.#markUserInput,
      { passive: true }
    );
    this.element.addEventListener(
      'wheel',
      this.#markUserInput,
      { passive: true }
    );
    this.element.addEventListener(
      'keydown',
      this.#markUserInput
    );
  }

  /**
   * Scroll to a position or element.
   *
   * @param {number | HTMLElement} input
   * @param {Object} [options]
   * @param {boolean} [options.instant]
   * @returns {Promise<void>}
   */
  async to(input, options = {}) {
    let value;

    if (input instanceof HTMLElement) {
      const paddingStart = calculatePaddingStart(
        this.element,
        this.axis
      );

      value =
        input[`offset${this.#edge}`] - paddingStart;
    } else {
      value = input;
    }

    const currentPosition =
      this.element[`scroll${this.#edge}`];

    if (Math.abs(currentPosition - value) < 1) {
      if (this.#isScrolling) {
        this.#finishScroll(false);
      }

      return;
    }

    return this.#scroll({
      ...options,
      method: 'scrollTo',
      value,
    });
  }

  /**
   * Scroll by a number of pixels.
   *
   * @param {number} value
   * @param {Object} [options]
   * @param {boolean} [options.instant]
   * @returns {Promise<void>}
   */
  by(value, options = {}) {
    return this.#scroll({
      ...options,
      method: 'scrollBy',
      value,
    });
  }

  /**
   * @param {Object} options
   * @param {'scrollTo' | 'scrollBy'} options.method
   * @param {number} options.value
   * @param {boolean} [options.instant]
   * @returns {Promise<void>}
   */
  #scroll(options) {
    const {
      method,
      value,
      instant = prefersReducedMotion(),
    } = options;

    this.#resetPromiseState();
    this.#ignore = instant;
    this.#userEvent = false;
    this.#lastScrollWasUserEvent = false;

    const edge = this.#edge;
    const currentPosition =
      this.element[`scroll${edge}`];
    const targetPosition =
      method === 'scrollBy'
        ? currentPosition + value
        : value;
    const scrollDistance = Math.abs(
      targetPosition - currentPosition
    );

    if (scrollDistance < 1) {
      this.#ignore = false;
      this.#userEvent = true;
      return Promise.resolve();
    }

    this.#setup();

    const property = edge.toLowerCase();

    if (instant) {
      scrollElementImmediately(
        this.element,
        method,
        property,
        value
      );

      /*
       * Some browsers do not emit a scroll event for an immediate update.
       * Resolve on the next frame without leaving #ignore active.
       */
      requestAnimationFrame(() => {
        if (this.#ignore) {
          this.#ignore = false;
          this.#finishScroll(false);
        }
      });
    } else {
      this.element[method]({
        [property]: value,
        behavior: 'smooth',
      });
    }

    return this.finished;
  }

  /**
   * @returns {'x' | 'y'}
   */
  get axis() {
    return getScrollAxis(this.element);
  }

  /**
   * @returns {Promise<void>}
   */
  get finished() {
    return this.#promise ?? Promise.resolve();
  }

  /**
   * @returns {'Left' | 'Top'}
   */
  get #edge() {
    return this.axis === 'x' ? 'Left' : 'Top';
  }

  #setup() {
    if (this.#promise) return;

    this.#promise = new Promise((resolve) => {
      this.#resolve = resolve;
    });
  }

  #resetPromiseState = () => {
    this.#handleScrollEnd.cancel();
    this.#resolve?.();

    this.#promise = undefined;
    this.#resolve = undefined;
  };

  #resetInteractionState = () => {
    this.#userEvent = true;
    this.#ignore = false;
    this.#lastScrollWasUserEvent = true;
  };

  #markUserInput = () => {
    /*
     * A real touch, pointer, wheel, or key action takes ownership from any
     * programmatic smooth scroll immediately.
     */
    this.#userEvent = true;
    this.#ignore = false;
    this.#lastScrollWasUserEvent = true;
  };

  #handleScroll = () => {
    if (!this.#isScrolling) {
      this.#onScrollInit?.();
      this.#isScrolling = true;
    }

    if (this.#ignore) {
      this.#ignore = false;
      this.#lastScrollWasUserEvent = false;

      if (this.#supportsNativeScrollEnd) {
        /*
         * Prefer the native scrollend signal, with the debounce retained as a
         * fallback for browsers that expose the event but fail to dispatch it
         * for an immediate movement.
         */
        this.#handleScrollEnd(false);
      } else {
        this.#finishScroll(false);
      }

      return;
    }

    const userEvent = this.#userEvent;
    this.#lastScrollWasUserEvent = userEvent;

    this.#setup();

    if (userEvent) {
      this.#throttledCallback();
    }

    if (!this.#supportsNativeScrollEnd) {
      this.#handleScrollEnd(userEvent);
    }
  };

  #handleNativeScrollEnd = () => {
    this.#handleScrollEnd.cancel();
    this.#finishScroll(
      this.#lastScrollWasUserEvent
    );
  };

  #finishScroll = (userEvent) => {
    this.#handleScrollEnd.cancel();

    this.#resolve?.();

    if (userEvent) {
      this.#endCallback();
    }

    this.#promise = undefined;
    this.#resolve = undefined;

    if (this.#isScrolling) {
      this.#onScrollEnd?.();
      this.#isScrolling = false;
    }

    this.#resetInteractionState();
  };

  #handleScrollEnd = debounce(
    /**
     * @param {boolean} userEvent
     */
    (userEvent) => {
      this.#finishScroll(userEvent);
    },
    SCROLL_END_TIMEOUT
  );

  /**
   * Enable or disable scroll snapping.
   *
   * @param {boolean} value
   */
  set snap(value) {
    this.#ignore = true;

    this.element.style.setProperty(
      'scroll-snap-type',
      value ? `${this.axis} mandatory` : 'none'
    );

    /*
     * A snap-style change does not always emit a scroll event. Never leave the
     * next genuine user scroll in an ignored state.
     */
    requestAnimationFrame(() => {
      this.#ignore = false;
    });
  }

  /**
   * Remove listeners and resolve pending work.
   */
  destroy() {
    this.element.removeEventListener(
      'scroll',
      this.#handleScroll
    );

    if (this.#supportsNativeScrollEnd) {
      this.element.removeEventListener(
        'scrollend',
        this.#handleNativeScrollEnd
      );
    }

    this.element.removeEventListener(
      'pointerdown',
      this.#markUserInput
    );
    this.element.removeEventListener(
      'touchstart',
      this.#markUserInput
    );
    this.element.removeEventListener(
      'wheel',
      this.#markUserInput
    );
    this.element.removeEventListener(
      'keydown',
      this.#markUserInput
    );

    this.#handleScrollEnd.cancel();
    this.#throttledCallback.cancel?.();
    this.#resolve?.();

    this.#promise = undefined;
    this.#resolve = undefined;
    this.#isScrolling = false;
  }
}

/**
 * Perform an immediate movement regardless of CSS scroll-behavior.
 *
 * @param {HTMLElement} element
 * @param {'scrollTo' | 'scrollBy'} method
 * @param {'left' | 'top'} property
 * @param {number} value
 */
function scrollElementImmediately(
  element,
  method,
  property,
  value
) {
  const previousScrollBehavior =
    element.style.scrollBehavior;

  element.style.scrollBehavior = 'auto';

  element[method]({
    [property]: value,
    behavior: 'auto',
  });

  requestAnimationFrame(() => {
    if (previousScrollBehavior) {
      element.style.scrollBehavior =
        previousScrollBehavior;
    } else {
      element.style.removeProperty(
        'scroll-behavior'
      );
    }
  });
}

/**
 * @param {HTMLElement} element
 * @returns {'x' | 'y'}
 */
function getScrollAxis(element) {
  const horizontalOverflow = Math.max(
    0,
    element.scrollWidth - element.clientWidth
  );
  const verticalOverflow = Math.max(
    0,
    element.scrollHeight - element.clientHeight
  );

  if (
    horizontalOverflow > 1 &&
    verticalOverflow <= 1
  ) {
    return 'x';
  }

  if (
    verticalOverflow > 1 &&
    horizontalOverflow <= 1
  ) {
    return 'y';
  }

  return horizontalOverflow > verticalOverflow
    ? 'x'
    : 'y';
}

/**
 * @param {HTMLElement} element
 * @param {'x' | 'y'} axis
 * @returns {number}
 */
function calculatePaddingStart(element, axis) {
  const computedStyle = getComputedStyle(element);
  const value =
    axis === 'x'
      ? computedStyle.paddingInlineStart
      : computedStyle.paddingBlockStart;
  const parsedValue = Number.parseFloat(value);

  return Number.isFinite(parsedValue)
    ? parsedValue
    : 0;
}

/**
 * Scroll an element into view.
 *
 * @param {Element} element
 * @param {Object} options
 * @param {ScrollBehavior} [options.behavior='smooth']
 * @param {'start' | 'center' | 'end'} [options.block='start']
 * @param {'start' | 'center' | 'end'} [options.inline='start']
 * @param {Element} [options.ancestor]
 */
export function scrollIntoView(
  element,
  {
    ancestor,
    behavior = 'smooth',
    block = 'start',
    inline = 'start',
  } = {}
) {
  const resolvedBehavior =
    prefersReducedMotion() ? 'auto' : behavior;

  if (!ancestor) {
    return element.scrollIntoView({
      behavior: resolvedBehavior,
      block,
      inline,
    });
  }

  const elemRect = element.getBoundingClientRect();
  const ancestorRect =
    ancestor.getBoundingClientRect();

  /**
   * @param {'start' | 'center' | 'end'} alignment
   * @param {number} ancestorStart
   * @param {number} ancestorLength
   * @param {number} elemStart
   * @param {number} elemLength
   * @param {number} currentScroll
   * @returns {number}
   */
  const calculateScrollOffset = (
    alignment,
    ancestorStart,
    ancestorLength,
    elemStart,
    elemLength,
    currentScroll
  ) => {
    switch (alignment) {
      case 'start':
        return (
          currentScroll +
          elemStart -
          ancestorStart
        );
      case 'center':
        return (
          currentScroll +
          elemStart -
          ancestorStart -
          ancestorLength / 2 +
          elemLength / 2
        );
      case 'end':
        return (
          currentScroll +
          elemStart -
          ancestorStart -
          ancestorLength +
          elemLength
        );
      default:
        return currentScroll;
    }
  };

  const scrollTop =
    ancestor.scrollHeight > ancestor.clientHeight
      ? calculateScrollOffset(
          block,
          ancestorRect.top,
          ancestor.clientHeight,
          elemRect.top,
          elemRect.height,
          ancestor.scrollTop
        )
      : ancestor.scrollTop;

  const scrollLeft =
    ancestor.scrollWidth > ancestor.clientWidth
      ? calculateScrollOffset(
          inline,
          ancestorRect.left,
          ancestor.clientWidth,
          elemRect.left,
          elemRect.width,
          ancestor.scrollLeft
        )
      : ancestor.scrollLeft;

  if (
    resolvedBehavior === 'auto' &&
    ancestor instanceof HTMLElement
  ) {
    const previousScrollBehavior =
      ancestor.style.scrollBehavior;

    ancestor.style.scrollBehavior = 'auto';

    ancestor.scrollTo({
      top: scrollTop,
      left: scrollLeft,
      behavior: 'auto',
    });

    requestAnimationFrame(() => {
      if (previousScrollBehavior) {
        ancestor.style.scrollBehavior =
          previousScrollBehavior;
      } else {
        ancestor.style.removeProperty(
          'scroll-behavior'
        );
      }
    });

    return;
  }

  ancestor.scrollTo({
    top: scrollTop,
    left: scrollLeft,
    behavior: resolvedBehavior,
  });
}

/**
 * Adds a directional fade to overflowing content.
 */
class ScrollHint extends HTMLElement {
  /** @type {number | null} */
  #rafId = null;

  connectedCallback() {
    this.addEventListener(
      'scroll',
      this.#handleScroll,
      { passive: true }
    );
    this.#resizeObserver.observe(this);
    this.#update();
  }

  disconnectedCallback() {
    this.removeEventListener(
      'scroll',
      this.#handleScroll
    );
    this.#resizeObserver.disconnect();

    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }

  #handleScroll = () => {
    if (this.#rafId !== null) return;

    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#update();
    });
  };

  #update = () => {
    const {
      scrollTop,
      scrollHeight,
      clientHeight,
      scrollLeft,
      scrollWidth,
      clientWidth,
    } = this;

    const isHorizontal = scrollWidth > clientWidth;
    const scrollRange = isHorizontal
      ? scrollWidth - clientWidth
      : scrollHeight - clientHeight;
    const currentPosition = isHorizontal
      ? scrollLeft
      : scrollTop;

    if (scrollRange <= 0) {
      this.style.maskImage = '';
      return;
    }

    const scrollPercentage = Math.min(
      1,
      Math.max(0, currentPosition / scrollRange)
    );
    const direction = isHorizontal
      ? 'right'
      : 'bottom';

    this.style.maskImage = `linear-gradient(
      to ${direction},
      transparent ${scrollPercentage > 0 ? 1 : 0}%,
      black ${
        scrollPercentage < 0.1
          ? scrollPercentage * 100
          : 10
      }%,
      black ${
        scrollPercentage > 0.9
          ? scrollPercentage * 100
          : 90
      }%,
      transparent 100%
    )`;
  };

  #resizeObserver = new ResizeObserver(() => {
    this.#update();
  });
}

if (!customElements.get('scroll-hint')) {
  customElements.define('scroll-hint', ScrollHint);
}